import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { mloPhotoUrl } from "@/lib/photo";
import { toast, Toaster } from "sonner";
import { ArrowRight, Loader2, LogOut, ExternalLink, User2, Camera, Save, BookOpen, KeyRound, Send, Eye, EyeOff, Copy, Sparkles, Users, Newspaper, Link as LinkIcon, LifeBuoy } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import OutreachHub from "@/components/dashboard/OutreachHub";
import GettingStarted from "@/components/dashboard/GettingStarted";
import CommunityCard from "@/components/dashboard/CommunityCard";
import ToolkitsCard from "@/components/dashboard/ToolkitsCard";
import MyPartnersCard from "@/components/dashboard/MyPartnersCard";
import PageStrip from "@/components/dashboard/PageStrip";
import PagePreviewCard from "@/components/dashboard/PagePreviewCard";
import { clearGoogleSession } from "@/lib/googleSession";
import ApplicantPipelineCard from "@/components/ApplicantPipelineCard";
import WelcomeVideoCard from "@/components/WelcomeVideoCard";
import RecruitPlaybookCard from "@/components/dashboard/RecruitPlaybookCard";
import AnnualCheckCard from "@/components/dashboard/AnnualCheckCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "grc_mlo_token";

export default function MLODashboard() {
  const [state, setState] = useState({ loading: true, user: null, profile: null });
  const [version, setVersion] = useState(0);
  const [guide, setGuide] = useState(null);
  const [reopen, setReopen] = useState(0);
  const nav = useNavigate();

  const authHeader = useCallback(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mlo/auth/me`, { headers: authHeader() });
      setState({ loading: false, user: data.user, profile: data.profile });
      setVersion((v) => v + 1);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      nav("/mlologin", { replace: true });
    }
  }, [authHeader, nav]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    clearGoogleSession();
    localStorage.removeItem(TOKEN_KEY);
    nav("/mlologin", { replace: true });
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  const { user, profile } = state;

  return (
    <div className="min-h-screen bg-linen text-navy antialiased" data-testid="mlo-dashboard-page">
      {/* Nav */}
      <header className="bg-white border-b border-linen-border">
        <div className="container-grc h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            {guide && (
              <button
                onClick={() => setReopen((v) => v + 1)}
                data-testid="mlo-getting-started-reopen"
                title="Show my setup checklist"
                className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-navy transition-colors"
              >
                <LifeBuoy size={14} />
                <span className="hidden sm:inline">Getting started</span>
                {!guide.complete && (
                  <span className="rounded-full bg-lime-soft border border-[#A6D24A] px-1.5 py-0.5 text-[10px] font-semibold text-navy tabular-nums">
                    {guide.done}/{guide.total}
                  </span>
                )}
              </button>
            )}
            <a
              href={`/${profile?.slug || ""}`}
              target="_blank"
              rel="noreferrer"
              data-testid="mlo-view-page-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-teal hover:underline"
            >
              View my page <ExternalLink size={13} />
            </a>
            <button
              onClick={logout}
              data-testid="mlo-logout-button"
              className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-navy"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-grc py-10 sm:py-14">
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-widest text-teal">MLO dashboard</p>
          <h1 className="mt-1 font-heading font-medium text-3xl sm:text-4xl text-navy">
            Welcome back, {profile?.name?.split(" ")[0] || "MLO"}.
          </h1>
          <p className="mt-2 text-sm text-ink-500">
            Your page and how it reads is up top. Your leads and your partners' activity sit under it.
            The messaging tools — drafts, toolkits, field updates — are further down for when you want them.
          </p>
        </div>

        <div className="mb-6">
          <PageStrip profile={profile} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Setup checklist leads while it's unfinished, then slims down and drops to the bottom. */}
            <div className={guide?.complete ? "order-last" : "order-first"}>
              <GettingStarted
                authHeader={authHeader}
                refreshKey={version}
                reopenKey={reopen}
                onStatus={setGuide}
              />
            </div>

            {/* 1 — Your page: the thing they came here to adjust */}
            <ProfileEditor
              profile={profile}
              authHeader={authHeader}
              onSaved={load}
            />
            <PhotoUploader profile={profile} authHeader={authHeader} onSaved={load} />
            <PagePreviewCard slug={profile?.slug} version={version} />

            {/* 2 — What's happening right now */}
            <MyLeadsCard authHeader={authHeader} profile={profile} />
            <ApplicantPipelineCard endpoint="/mlo/applicant-pipeline" authHeader={authHeader} scoped />
            <MyPartnersCard authHeader={authHeader} />
            <AnnualCheckCard authHeader={authHeader} />

            {/* 3 — Messaging and materials, for when they're ready to work on outreach */}
            <RecruitPlaybookCard authHeader={authHeader} />
            <WelcomeVideoCard scope="mlo" authHeader={authHeader} />
            <OutreachHub authHeader={authHeader} />
            <CopyAssist authHeader={authHeader} profile={profile} />
            <ToolkitsCard authHeader={authHeader} />
            <FieldUpdatesCard authHeader={authHeader} />
            <SendToBDMTile />
          </div>
          <aside className="space-y-6">
            <MyLinksCard slug={profile?.slug} />
            <PrintAndLibraryCard slug={profile?.slug} />
            <CommunityCard />
            <ChangePasswordCard authHeader={authHeader} />
          </aside>
        </div>
      </main>

      <Toaster position="top-center" richColors />
    </div>
  );
}

function MyLeadsCard({ authHeader, profile }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/mlo/leads`, { headers: authHeader() })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [authHeader]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6 sm:p-8" data-testid="mlo-my-leads-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <Users size={13} /> Your pipeline
      </div>
      <h2 className="mt-1 font-heading font-medium text-2xl text-navy">
        Leads from your page
        {rows ? <span className="ml-2 text-sm font-body text-ink-500 tabular-nums">{rows.length}</span> : null}
      </h2>
      <p className="mt-1 text-sm text-ink-500">
        Everyone who submitted their details on greatratecheck.com/{profile?.slug || "…"}.
      </p>

      {rows === null ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-teal" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-5 rounded-2xl border border-dashed border-linen-border bg-linen/50 px-4 py-6 text-sm text-ink-500 text-center" data-testid="mlo-my-leads-empty">
          No leads yet. Share your page link and they'll land right here.
        </p>
      ) : (
        <ul className="mt-5 divide-y divide-linen-border" data-testid="mlo-my-leads-list">
          {rows.slice(0, 15).map((l) => (
            <li key={l.id} className="py-3 flex items-center gap-3" data-testid={`mlo-lead-${l.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-navy truncate">{l.full_name}</div>
                <div className="text-[11px] text-ink-500 truncate">
                  {l.email}
                  {l.phone ? ` · ${l.phone}` : ""}
                </div>
              </div>
              <a
                href={`tel:${(l.phone || "").replace(/[^+0-9]/g, "")}`}
                data-testid={`mlo-lead-call-${l.id}`}
                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold bg-navy text-white px-2.5 py-1.5 rounded-full hover:bg-[#173a5f] transition-colors"
              >
                Call
              </a>
              <span className="shrink-0 text-[11px] text-ink-400 tabular-nums w-12 text-right">{fmt(l.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FieldUpdatesCard({ authHeader }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/library/field-updates?limit=6`, { headers: authHeader() })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [authHeader]);

  if (rows === null || rows.length === 0) return null;

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6 sm:p-8" data-testid="mlo-field-updates-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <Newspaper size={13} /> Operating rhythm
      </div>
      <h2 className="mt-1 font-heading font-medium text-2xl text-navy">Weekly Field Updates</h2>
      <p className="mt-1 text-sm text-ink-500">
        Every send has a video and a written brief. Open one and copy the script straight out of it.
      </p>
      <ul className="mt-5 divide-y divide-linen-border" data-testid="mlo-field-updates-list">
        {rows.map((f) => (
          <li key={f.id} className="py-3 flex items-center gap-3" data-testid={`mlo-field-update-${f.id}`}>
            {f.youtube_id ? (
              <img
                src={`https://img.youtube.com/vi/${f.youtube_id}/default.jpg`}
                alt=""
                className="shrink-0 w-16 h-10 rounded-lg object-cover border border-linen-border"
                loading="lazy"
              />
            ) : (
              <span className="shrink-0 w-16 h-10 rounded-lg bg-navy/90 flex items-center justify-center text-white/70">
                <Newspaper size={14} />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-navy truncate">{f.subject}</div>
              <div className="text-[11px] text-ink-500">
                {(f.created_at || "").slice(0, 10)}
                {f.brief ? " · written brief included" : ""}
              </div>
            </div>
            <Link
              to={`/fieldupdate/${f.id}`}
              data-testid={`mlo-field-update-open-${f.id}`}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold bg-navy text-white px-2.5 py-1.5 rounded-full hover:bg-[#173a5f] transition-colors"
            >
              Read <ArrowRight size={11} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProfileEditor({ profile, authHeader, onSaved }) {  const [name, setName] = useState(profile?.name || "");
  const [title, setTitle] = useState(profile?.title || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [email, setEmail] = useState(profile?.email || "");
  const [city, setCity] = useState(profile?.city || "");
  const [bio, setBio] = useState(profile?.bio || "");
  const [tagline, setTagline] = useState(profile?.tagline || "");
  const [ogHeadline, setOgHeadline] = useState(profile?.og_headline || "");
  const [applyUrl, setApplyUrl] = useState(profile?.apply_url || "");
  const [licensedStates, setLicensedStates] = useState((profile?.licensed_states || []).join(", "));
  const [acceptingLeads, setAcceptingLeads] = useState(profile?.accepting_leads ?? true);
  const [yearsExperience, setYearsExperience] = useState(profile?.years_experience ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await axios.patch(
        `${API}/mlo/profile`,
        {
          name: name.trim(),
          title: title.trim(),
          phone: phone.trim(),
          email: email.trim(),
          city: city.trim(),
          bio: bio.trim(),
          tagline: tagline.trim(),
          og_headline: ogHeadline.trim(),
          apply_url: applyUrl.trim(),
          licensed_states: licensedStates.split(",").map((x) => x.trim()).filter(Boolean),
          accepting_leads: acceptingLeads,
          years_experience: yearsExperience === "" ? null : Number(yearsExperience),
        },
        { headers: authHeader() },
      );
      toast.success("Profile saved");
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="bg-white border border-linen-border rounded-3xl shadow-soft p-6 sm:p-8"
      data-testid="mlo-profile-editor"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <User2 size={13} /> Your page
      </div>
      <h2 className="mt-1 font-heading font-medium text-2xl text-navy">Profile & copy</h2>
      <p className="mt-1 text-sm text-ink-500">
        Everything a visitor sees on greatratecheck.com/{profile?.slug || "…"}.
      </p>

      <div className="mt-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name" value={name} onChange={setName} testid="mlo-field-name" />
          <Field label="Title" value={title} onChange={setTitle} testid="mlo-field-title" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Phone" value={phone} onChange={setPhone} placeholder="(425) 555-1234" testid="mlo-field-phone" />
          <div>
            <Field label="Email (shown on your page)" value={email} onChange={setEmail} type="email" placeholder="you@yourdomain.com" testid="mlo-field-email" />
            <p className="text-[11px] text-ink-500 mt-1">This is your display email — it doesn't change the email you sign in with. Leave blank to hide it from your page.</p>
          </div>
        </div>
        <div>
          <Field label="City, State (shown in your page footer)" value={city} onChange={setCity} placeholder="e.g. Tampa, FL" testid="mlo-field-city" />
          <p className="text-[11px] text-ink-500 mt-1">Leave blank to show no location.</p>
        </div>
        <div>
          <Field
            label="My loan application link"
            value={applyUrl}
            onChange={setApplyUrl}
            placeholder="https://prod.lendingpad.com/…/guest-application"
            testid="mlo-field-apply-url"
          />
          <p className="text-[11px] text-ink-500 mt-1">
            Your own application URL. This is where the Apply buttons on your page send borrowers. Leave it
            blank and those buttons become "Call {profile?.name?.split(" ")[0] || "me"}" instead — your
            borrowers are never sent to another loan officer's application.
          </p>
        </div>
        <div className="rounded-2xl border border-linen-border bg-linen/40 p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-teal">Lead routing</div>
          <Field
            label="States you're licensed in"
            value={licensedStates}
            onChange={(v) => setLicensedStates(v.toUpperCase())}
            placeholder="WA, AZ, CA"
            testid="mlo-field-licensed-states"
          />
          <p className="text-[11px] text-ink-500">
            Two-letter codes, comma separated. When someone checks their rate on the main Great Rate Check page,
            we only match them with officers licensed in their property's state.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={acceptingLeads}
              onChange={(e) => setAcceptingLeads(e.target.checked)}
              data-testid="mlo-field-accepting-leads"
              className="accent-[#1F8F8B]"
            />
            <span className="text-navy">I'm accepting new leads from the main page</span>
          </label>
        </div>
        <Field label="Tagline (short — one line)" value={tagline} onChange={setTagline} testid="mlo-field-tagline" />
        <div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">Years of experience (optional)</span>
            <input
              type="number"
              min={0}
              max={80}
              value={yearsExperience}
              onChange={(e) => setYearsExperience(e.target.value)}
              placeholder="e.g. 30"
              data-testid="mlo-field-years-experience"
              className="mt-1 w-full sm:max-w-[180px] rounded-xl border border-linen-border p-3 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none transition-colors"
            />
          </label>
          <p className="text-[11px] text-ink-500 mt-1">Shows as an "N+ yrs" pill on your Hero card and a "Years originating" stat in the Meet section. Leave blank to hide.</p>
        </div>
        <FieldArea label="Bio (shown in the Meet section)" value={bio} onChange={setBio} rows={5} testid="mlo-field-bio" />
        <div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">OG share headline (link preview)</span>
            <textarea
              value={ogHeadline}
              onChange={(e) => setOgHeadline(e.target.value)}
              rows={2}
              placeholder="Check your rate | before you choose | your loan."
              data-testid="mlo-field-og-headline"
              className="mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none transition-colors"
            />
          </label>
          <p className="text-[11px] text-ink-500 mt-1">
            Use <code>|</code> to break lines. Shown on your shared link preview. Regenerates on save.
          </p>
        </div>
      </div>

      {err && (
        <p className="mt-4 text-sm text-red-600" data-testid="mlo-profile-error">
          {err}
        </p>
      )}

      <div className="mt-6 flex items-center justify-end">
        <button
          type="submit"
          disabled={busy}
          data-testid="mlo-profile-save"
          className="inline-flex items-center gap-2 bg-navy hover:bg-[#173a5f] text-white font-semibold text-sm px-6 py-3 rounded-full shadow-cta transition-all disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={15} />}
          Save changes
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-ink-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none transition-colors"
      />
    </label>
  );
}

function FieldArea({ label, value, onChange, rows = 4, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-ink-500">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={testid}
        className="mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none transition-colors"
      />
    </label>
  );
}

function PhotoUploader({ profile, authHeader, onSaved }) {
  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="mlo-photo-uploader">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <Camera size={13} /> Photos
      </div>
      <h3 className="mt-1 font-heading font-medium text-xl text-navy">Photos & headshots</h3>
      <p className="mt-1 text-sm text-ink-500">
        The <strong>Primary</strong> shows on the hero card + link previews. The <strong>Meet-section</strong> photo is
        optional — falls back to primary if unset.
      </p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <PhotoSlot
          label="Primary photo"
          slot="primary"
          hasPhoto={profile?.has_photo}
          currentUrl={mloPhotoUrl(profile)}
          authHeader={authHeader}
          onSaved={onSaved}
        />
        <PhotoSlot
          label="Meet-section photo (optional)"
          slot="secondary"
          hasPhoto={profile?.has_photo_secondary}
          currentUrl={mloPhotoUrl(profile, "secondary")}
          authHeader={authHeader}
          onSaved={onSaved}
        />
      </div>
    </div>
  );
}

function PhotoSlot({ label, slot, hasPhoto, currentUrl, authHeader, onSaved }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("slot", slot);
      await axios.post(`${API}/mlo/profile/photo`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`${slot === "primary" ? "Primary" : "Meet-section"} photo updated`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-4">
      <div className="w-16 h-20 rounded-xl overflow-hidden bg-linen border border-linen-border shrink-0">
        {hasPhoto && currentUrl ? (
          <img src={currentUrl} alt={label} className="w-full h-full object-cover object-top" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ink-500">
            <User2 size={18} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-navy">{label}</p>
        <p className="text-[11px] text-ink-500 mt-0.5">{hasPhoto ? "Currently uploaded" : "Not set"}</p>
        <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-teal hover:underline cursor-pointer">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`mlo-photo-input-${slot}`}
            onChange={(e) => upload(e.target.files?.[0] || null)}
            disabled={busy}
          />
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {hasPhoto ? "Replace photo" : "Upload photo"}
        </label>
      </div>
    </div>
  );
}

function SendToBDMTile() {
  return (
    <Link
      to="/mlodashboard/library"
      data-testid="mlo-send-to-bdm-tile"
      className="group block rounded-3xl overflow-hidden border border-linen-border shadow-soft hover:shadow-lg transition"
      style={{ background: "linear-gradient(135deg, #0F2A47 0%, #17385e 60%, #1F8F8B 130%)" }}
    >
      <div className="p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <div className="shrink-0 w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
          <Send className="w-7 h-7 text-[#A6D24A]" strokeWidth={1.6} />
        </div>
        <div className="flex-1 text-white">
          <div className="text-[11px] uppercase tracking-widest text-[#A6D24A]">Partner support</div>
          <h3 className="mt-0.5 font-heading font-medium text-xl sm:text-2xl">Send assets to a BDM</h3>
          <p className="mt-1 text-sm text-white/80 max-w-md">
            Issue any marketing packet, message pack, or process doc directly to your BDMs — signed with your name and contact info.
          </p>
        </div>
        <div className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white/95 text-[#0F2A47] px-4 py-2 text-sm font-medium group-hover:bg-white transition">
          Open library <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}

function MyLinksCard({ slug }) {
  const origin = window.location.origin;
  const MY_LINKS = [
    { label: "My branded page", url: `${origin}/${slug || ""}`, use: "Your introduction. Text this to anyone who asks what you do." },
    { label: "My borrower apply page", url: `${origin}/${slug || ""}/apply`, use: "For someone ready to start an application with you." },
    { label: "My partner referral link", url: `${origin}/${slug || ""}/partnerapply`, use: "For an agent or connector you want as a BDM on your team." },
  ];
  const copy = (url) =>
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Couldn't copy — open and copy from the address bar."));
  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="mlo-my-links-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <LinkIcon size={13} /> My links
      </div>
      <h3 className="mt-1 font-heading font-medium text-xl text-navy">The three you send most</h3>
      <p className="mt-1 text-xs text-ink-500">Save these in your phone. Each one unfurls with your own branded preview card.</p>
      <ul className="mt-4 space-y-2">
        {MY_LINKS.map((l) => (
          <li key={l.url} className="rounded-xl border border-linen-border px-3 py-2.5" data-testid={`mlo-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-navy truncate">{l.label}</div>
                <div className="text-[11px] text-teal truncate">{l.url}</div>
              </div>
              <button
                onClick={() => copy(l.url)}
                data-testid={`mlo-link-copy-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold bg-navy text-white px-2.5 py-1.5 rounded-full hover:bg-navy/85 transition-colors"
              >
                <Copy size={11} /> Copy
              </button>
              <a href={l.url} target="_blank" rel="noreferrer" className="shrink-0 p-1 text-ink-400 hover:text-teal transition-colors" title="Open">
                <ArrowRight size={14} />
              </a>
            </div>
            <p className="mt-1 text-[11.5px] text-ink-500 leading-snug">{l.use}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PrintAndLibraryCard({ slug }) {
  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="mlo-print-library-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <BookOpen size={13} /> Print & library
      </div>
      <h3 className="mt-1 font-heading font-medium text-xl text-navy">Assets to hand out</h3>
      <p className="mt-1 text-sm text-ink-500">
        Print-ready pieces with your name and photo already on them, plus the shared asset library.
      </p>
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-widest text-ink-500">My print pieces</div>
        <ul className="mt-2 space-y-1.5" data-testid="mlo-print-pieces">
          {[
            ["openhouse", "Open house card"],
            ["checkup", "Annual rate-checkup postcard"],
            ["closinggift", "Closing-gift card"],
          ].map(([piece, label]) => (
            <li key={piece}>
              <a
                href={`${API}/mlos/${slug}/print/${piece}.pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid={`mlo-print-${piece}`}
                className="inline-flex items-center gap-1.5 text-sm text-navy hover:text-teal"
              >
                {label} (PDF) <ArrowRight size={13} />
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-5 pt-4 border-t border-linen-border space-y-2">
        <Link to="/mlodashboard/library" className="inline-flex items-center gap-1.5 text-sm text-navy hover:text-teal">
          Send assets to a BDM <ArrowRight size={13} />
        </Link>
        <br />
        <a href="/library" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-navy hover:text-teal">
          Public marketing library <ArrowRight size={13} />
        </a>
      </div>
    </div>
  );
}

function ChangePasswordCard({ authHeader }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next !== confirm) {
      setErr("New password and confirmation don't match.");
      return;
    }
    if (next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/mlo/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeader() },
      );
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm(""); setOpen(false);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Password change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="mlo-change-password-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <KeyRound size={13} /> Security
      </div>
      <div className="mt-1 flex items-center justify-between">
        <h3 className="font-heading font-medium text-xl text-navy">Password</h3>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="mlo-change-password-toggle"
            className="text-xs text-teal hover:underline"
          >
            Change
          </button>
        )}
      </div>
      {!open ? (
        <p className="mt-2 text-sm text-ink-500">Keep your account secure — change your password anytime.</p>
      ) : (
        <form onSubmit={submit} className="mt-3 space-y-3">
          <PWField label="Current password" value={current} onChange={setCurrent} testid="mlo-pw-current" />
          <PWField label="New password (8+ chars)" value={next} onChange={setNext} testid="mlo-pw-new" />
          <PWField label="Confirm new password" value={confirm} onChange={setConfirm} testid="mlo-pw-confirm" />
          {err && <p className="text-xs text-red-600" data-testid="mlo-pw-error">{err}</p>}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setErr(""); setCurrent(""); setNext(""); setConfirm(""); }}
              className="text-xs px-3 py-1.5 rounded-full text-navy hover:bg-linen"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !current || !next || !confirm}
              data-testid="mlo-pw-submit"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Update password
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function PWField({ label, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-ink-500">{label}</span>
      <div className="relative mt-1">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full rounded-xl border border-linen-border p-2.5 pr-10 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none transition-colors"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Hide password" : "Show password"}
          data-testid={`${testid}-toggle`}
          className="absolute inset-y-0 right-2 flex items-center px-2 text-ink-500 hover:text-navy"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}


function CopyAssist({ authHeader, profile }) {
  const origin = window.location.origin;
  const [channel, setChannel] = useState("instagram");
  const [about, setAbout] = useState("A quick, free mortgage rate checkup — two minutes, no credit pull");
  const [linkChoice, setLinkChoice] = useState("page");
  const [audience, setAudience] = useState("");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState("");
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    setShowHistory(true);
    try {
      const { data } = await axios.get(`${API}/mlo/copy-assist/history`, { headers: authHeader() });
      setHistory(data || []);
    } catch {
      setHistory([]);
    }
  };

  const LINKS = {
    page: { label: "My branded page", url: `${origin}/${profile?.slug || ""}` },
    referral: { label: "My partner referral link", url: `${origin}/${profile?.slug || ""}/partnerapply` },
    none: { label: "No link", url: "" },
  };
  const ABOUT_PRESETS = [
    "A quick, free mortgage rate checkup — two minutes, no credit pull",
    "Inviting past clients to do an annual rate checkup",
    "Reminding my sphere I'm here for mortgage questions",
    "Recruiting a real estate agent to become a Great Rate Check partner",
    "Following up after an open house",
  ];

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/mlo/copy-assist`,
        { channel, about, link: LINKS[linkChoice].url, audience, extra },
        { headers: authHeader() },
      );
      setOutput(data.text || "");
      setHistory(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyOut = () =>
    navigator.clipboard
      .writeText(output)
      .then(() => toast.success("Copied — paste it anywhere"))
      .catch(() => toast.error("Couldn't copy"));

  const selectCls =
    "mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none transition-colors";

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6 sm:p-8" data-testid="mlo-copy-assist">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <Sparkles size={13} /> AI Copy Assist
      </div>
      <h2 className="mt-1 font-heading font-medium text-2xl text-navy">Draft a message</h2>
      <p className="mt-1 text-sm text-ink-500">
        On-brand, compliance-safe copy for social, texts, and emails — never quotes rates or promises savings.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Format</span>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="copy-assist-channel" className={selectCls}>
            <option value="instagram">Instagram caption</option>
            <option value="facebook">Facebook post</option>
            <option value="sms">Text message</option>
            <option value="email">Outreach email</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Include link</span>
          <select value={linkChoice} onChange={(e) => setLinkChoice(e.target.value)} data-testid="copy-assist-link" className={selectCls}>
            {Object.entries(LINKS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="block mt-3">
        <span className="text-xs uppercase tracking-widest text-ink-500">What's it about?</span>
        <select
          value={ABOUT_PRESETS.includes(about) ? about : "__custom"}
          onChange={(e) => e.target.value !== "__custom" && setAbout(e.target.value)}
          data-testid="copy-assist-about-preset"
          className={selectCls}
        >
          {ABOUT_PRESETS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
          <option value="__custom">Custom (edit below)</option>
        </select>
        <input
          value={about}
          onChange={(e) => setAbout(e.target.value)}
          data-testid="copy-assist-about"
          className={selectCls}
          placeholder="Describe what you're promoting…"
        />
      </label>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Audience (optional)</span>
          <input value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="copy-assist-audience" className={selectCls} placeholder="e.g. past clients, realtors…" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Anything else? (optional)</span>
          <input value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="copy-assist-extra" className={selectCls} placeholder="e.g. mention it's spring market…" />
        </label>
      </div>

      <button
        onClick={generate}
        disabled={busy || !about.trim()}
        data-testid="copy-assist-generate"
        className="mt-5 inline-flex items-center gap-2 bg-navy hover:bg-[#173a5f] text-white font-semibold text-sm px-6 py-3 rounded-full shadow-cta transition-all disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles size={15} />}
        {output ? "Regenerate" : "Draft it for me"}
      </button>
      <button
        onClick={loadHistory}
        data-testid="copy-assist-history-toggle"
        className="mt-5 ml-3 inline-flex items-center gap-1.5 text-sm text-navy hover:text-teal font-medium transition-colors"
      >
        {showHistory ? "Hide past drafts" : "Past drafts"}
      </button>

      {showHistory && (
        <div className="mt-4 rounded-2xl border border-linen-border divide-y divide-linen-border max-h-80 overflow-y-auto" data-testid="copy-assist-history">
          {history === null ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-teal" /></div>
          ) : history.length === 0 ? (
            <p className="px-4 py-6 text-sm text-ink-400 text-center" data-testid="copy-assist-history-empty">
              No drafts yet — generate your first one above.
            </p>
          ) : (
            history.map((h) => (
              <div key={h.id} className="px-4 py-3" data-testid={`copy-assist-history-item-${h.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[11px] text-ink-500">
                    <span className="uppercase tracking-widest font-semibold text-teal">{h.channel}</span>
                    {" · "}{new Date(h.created_at).toLocaleDateString()} · {h.about?.length > 45 ? `${h.about.slice(0, 45)}…` : h.about}
                  </div>
                  <button
                    onClick={() =>
                      navigator.clipboard.writeText(h.output).then(() => toast.success("Draft copied")).catch(() => toast.error("Couldn't copy"))
                    }
                    data-testid={`copy-assist-history-copy-${h.id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold bg-navy text-white px-2.5 py-1 rounded-full hover:bg-[#173a5f] transition-colors"
                  >
                    <Copy size={10} /> Copy
                  </button>
                </div>
                <p className="mt-1.5 text-[13px] text-navy whitespace-pre-wrap leading-relaxed line-clamp-3">{h.output}</p>
              </div>
            ))
          )}
        </div>
      )}

      {output && (
        <div className="mt-5 rounded-2xl bg-linen border border-linen-border p-5" data-testid="copy-assist-output">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-[11px] uppercase tracking-widest text-ink-500">Your draft</span>
            <button
              onClick={copyOut}
              data-testid="copy-assist-copy"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-navy text-white px-3 py-1.5 rounded-full hover:bg-[#173a5f] transition-colors"
            >
              <Copy size={11} /> Copy
            </button>
          </div>
          <p className="text-sm text-navy whitespace-pre-wrap leading-relaxed">{output}</p>
          <p className="mt-3 text-[11px] text-ink-500">
            Always give it a quick read before posting — you're responsible for what goes out under your name.
          </p>
        </div>
      )}
    </div>
  );
}
